/**
 * PublishDiffModal — "What will change on the live site?" preview.
 *
 * Called from PublishBar when the vendor clicks "Publish". Shows:
 *   • Sections ADDED since last publish (green)
 *   • Sections EDITED since last publish (amber)
 *   • Sections REMOVED since last publish (red)
 *   • Sections UNCHANGED (muted, collapsed by default)
 *
 * Confirming from this modal calls the parent's `onConfirm` (which runs the
 * real publish request). Cancel closes without publishing.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  X, Plus, Minus, Pencil, Circle, Send, Loader2, GitBranch,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CARD = 'rounded-2xl border border-white/[0.10] shadow-[0_30px_80px_-8px_rgba(0,0,0,0.7)] backdrop-blur-[18px] bg-[rgba(20,16,25,0.96)]';

function DiffRow({ icon: Icon, tone, label, count, children }) {
  const tones = {
    green: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10',
    amber: 'text-amber-300 border-amber-400/40 bg-amber-500/10',
    red: 'text-red-300 border-red-400/40 bg-red-500/10',
    muted: 'text-white/50 border-white/15 bg-white/5',
  }[tone] || 'text-white/70';
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold border ${tones}`}>
          <Icon size={11} /> {label}
        </span>
        <span className="text-white/40 text-[11px]">{count} {count === 1 ? 'section' : 'sections'}</span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Item({ summary, before_summary, type, tone }) {
  const dot = {
    added: 'bg-emerald-400',
    removed: 'bg-red-400',
    edited: 'bg-amber-400',
    unchanged: 'bg-white/25',
  }[tone];
  return (
    <div className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] border border-white/5">
      <span className={`w-1.5 h-1.5 rounded-full mt-1.5 ${dot}`} />
      <div className="min-w-0 flex-1">
        <p className="text-white text-[13px] font-medium truncate">{summary || '(untitled section)'}</p>
        <p className="text-white/40 text-[10px] uppercase tracking-wider mt-0.5">
          {(type || 'section').replace(/_/g, ' ')}
          {tone === 'edited' && before_summary && before_summary !== summary && (
            <span className="ml-2 text-white/30 normal-case tracking-normal italic">was: “{before_summary}”</span>
          )}
        </p>
      </div>
    </div>
  );
}

export default function PublishDiffModal({ open, onClose, onConfirm, publishing }) {
  const [loading, setLoading] = useState(false);
  const [diff, setDiff] = useState(null);
  const [meta, setMeta] = useState({ before: null, after: null });
  const [showUnchanged, setShowUnchanged] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/builder/versions/diff/current`, { headers: authH() });
        if (cancelled) return;
        setDiff(r.data?.diff || null);
        setMeta({ before: r.data?.before || null, after: r.data?.after || null });
      } catch (err) {
        setDiff({ added: [], removed: [], edited: [], unchanged: [], counts: { added: 0, removed: 0, edited: 0, unchanged: 0 } });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const counts = diff?.counts || { added: 0, removed: 0, edited: 0, unchanged: 0 };
  const totalChanges = counts.added + counts.edited + counts.removed;
  const isFirstPublish = !meta.before;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 sm:p-10 overflow-y-auto"
      onClick={onClose}
      data-testid="publish-diff-modal"
    >
      <div
        className={`w-full max-w-2xl ${CARD} my-8`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center shadow-[0_6px_20px_rgba(139,92,246,0.4)]">
              <GitBranch size={17} className="text-white" />
            </div>
            <div>
              <h2 className="text-white text-base font-bold tracking-tight">Review changes before publish</h2>
              <p className="text-white/50 text-xs">
                {isFirstPublish
                  ? 'This is your first publish — everything below will go live.'
                  : totalChanges === 0
                  ? 'No changes since your last publish.'
                  : `${totalChanges} change${totalChanges === 1 ? '' : 's'} will hit the live site.`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-white/50 hover:text-white hover:bg-white/5 flex items-center justify-center"
            data-testid="publish-diff-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 max-h-[52vh] overflow-y-auto" data-testid="publish-diff-body">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-white/40 text-sm">
              <Loader2 size={16} className="animate-spin mr-2" /> Comparing to last published…
            </div>
          ) : !diff ? (
            <div className="text-center py-14 text-white/40 text-sm">Could not compute diff.</div>
          ) : (
            <>
              {counts.added > 0 && (
                <DiffRow icon={Plus} tone="green" label="Added" count={counts.added}>
                  {diff.added.map((b) => (
                    <Item key={b.id} tone="added" summary={b.summary} type={b.type} />
                  ))}
                </DiffRow>
              )}
              {counts.edited > 0 && (
                <DiffRow icon={Pencil} tone="amber" label="Edited" count={counts.edited}>
                  {diff.edited.map((b) => (
                    <Item key={b.id} tone="edited" summary={b.summary} before_summary={b.before_summary} type={b.type} />
                  ))}
                </DiffRow>
              )}
              {counts.removed > 0 && (
                <DiffRow icon={Minus} tone="red" label="Removed" count={counts.removed}>
                  {diff.removed.map((b) => (
                    <Item key={b.id} tone="removed" summary={b.summary} type={b.type} />
                  ))}
                </DiffRow>
              )}
              {counts.unchanged > 0 && (
                <div>
                  <button
                    onClick={() => setShowUnchanged((v) => !v)}
                    className="w-full text-left text-white/40 hover:text-white/70 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 mb-2"
                    data-testid="publish-diff-toggle-unchanged"
                  >
                    <Circle size={10} /> {counts.unchanged} unchanged {showUnchanged ? '· hide' : '· show'}
                  </button>
                  {showUnchanged && (
                    <div className="space-y-1 opacity-60">
                      {diff.unchanged.map((b) => (
                        <Item key={b.id} tone="unchanged" summary={b.summary} type={b.type} />
                      ))}
                    </div>
                  )}
                </div>
              )}
              {totalChanges === 0 && !isFirstPublish && (
                <div className="text-center py-10 text-white/40 text-sm">
                  Your live site already matches your draft — nothing new to publish.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/8">
          <div className="text-white/40 text-[11px]">
            {meta.before?.created_at && (
              <>Last published <span className="text-white/60">{new Date(meta.before.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span></>
            )}
            {!meta.before && <>No previous publish on record</>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="h-9 px-3.5 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] text-white/60 hover:text-white hover:bg-white/5 border border-white/10"
              data-testid="publish-diff-cancel"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={publishing || (totalChanges === 0 && !isFirstPublish)}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-[0_6px_20px_rgba(139,92,246,0.35)] hover:shadow-[0_8px_24px_rgba(139,92,246,0.5)] disabled:opacity-40 disabled:shadow-none"
              data-testid="publish-diff-confirm"
            >
              {publishing ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Publish now
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
